/**
 * Leitura da foto escolhida num campo de arquivo, validada pelo schema do contrato.
 *
 * Papel: `handleArquivoDoItem` lê o arquivo como base64 e só entrega a imagem
 * a quem chamou depois de `fotoDeEntradaDoPedidoSchema` aceitá-la.
 *
 * Como usar: é o manipulador `onChange` de um `<input type="file" accept="image/*">`.
 * Quem recebe a imagem decide o que fazer com a recusa — o pedido de compra mostra
 * a mensagem ao lado da foto, e a solicitação do funcionário faz o mesmo. A
 * validação acontece aqui, e não só no envio, porque uma imagem grande demais
 * recusada só no `handleSubmit` falharia sem explicação.
 *
 * Mora em `compras` e é exportada pelo `index.ts` do módulo, porque
 * `solicitacao-de-compra` reaproveita o mesmo tratamento sem alcançar o arquivo
 * interno.
 */
import { type ChangeEvent } from 'react';

import { fotoDeEntradaDoPedidoSchema } from '@concierge/shared';

/**
 * Lê o arquivo escolhido como imagem em base64, valida contra
 * `fotoDeEntradaDoPedidoSchema` e entrega o resultado ao formulário.
 *
 * Quem chama é a caixa de foto de cada item, ao trocar o arquivo do `<input
 * type="file">` escondido dentro dela. A validação acontece aqui, e não só no
 * envio, para a recusa aparecer ao lado da foto no mesmo instante; um arquivo
 * que o navegador não consegue ler cai no mesmo `onInvalido`, com mensagem
 * própria.
 *
 * @param evento - O evento de troca do campo de arquivo.
 * @param onLido - Chamado com a imagem em base64, já validada.
 * @param onInvalido - Chamado com a mensagem a mostrar quando o arquivo não
 *   passa no schema ou não pôde ser lido.
 */
export function handleArquivoDoItem(
  evento: ChangeEvent<HTMLInputElement>,
  onLido: (imagem: string) => void,
  onInvalido: (mensagem: string) => void,
) {
  const arquivo = evento.target.files?.[0];
  if (arquivo === undefined) return;

  const leitor = new FileReader();
  leitor.onload = () => {
    if (typeof leitor.result !== 'string') return;
    const validado = fotoDeEntradaDoPedidoSchema.safeParse(leitor.result);
    if (!validado.success) {
      onInvalido(validado.error.issues[0]?.message ?? 'Não foi possível usar esta imagem.');
      return;
    }
    onLido(leitor.result);
  };
  leitor.onerror = () => {
    onInvalido('Não foi possível ler o arquivo.');
  };
  leitor.readAsDataURL(arquivo);
}
